// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useStepsData } from '@shared/ui-kit';
import { useAccountWorkflowData } from '../../providers';
import { useMemo } from 'react';
import type {
  AccountWorkflowBlock,
  AccountWorkflowKind,
  AccountWorkflowPhase,
  AccountWorkflowSection,
} from '../../types';

export const useProgressTracker = () => {
  const { blocks, workflow } = useAccountWorkflowData();
  const { stepIndex, step } = useStepsData();
  const stepPhase = step?.meta?.phase as AccountWorkflowPhase | undefined;
  const stepSection = step?.meta?.section as AccountWorkflowSection | undefined;

  const displayWorkflow = useMemo((): AccountWorkflowKind | 'profiling' => {
    if (workflow === 'combined') {
      return stepPhase === 'profiling' ? 'profiling' : 'account-opening';
    }

    return workflow;
  }, [stepPhase, workflow]);

  const currentBlockIndex = useMemo(() => {
    const index = blocks.findIndex(block => block.id === stepSection);

    return index === -1 ? stepIndex : index;
  }, [blocks, stepIndex, stepSection]);

  const stepCurrent = currentBlockIndex + 1;
  const stepTotal = blocks.length;

  const progress = useMemo(() => {
    if (stepTotal === 0) {
      return 0;
    }

    return Math.round((stepCurrent / stepTotal) * 100);
  }, [stepCurrent, stepTotal]);

  const currentBlock = useMemo((): AccountWorkflowBlock | undefined => {
    return (
      blocks.find(block => block.id === stepSection) ?? blocks[currentBlockIndex]
    );
  }, [blocks, currentBlockIndex, stepSection]);

  const showPhaseOverline = workflow === 'combined';
  const flowPhaseNumber = displayWorkflow === 'profiling' ? 2 : 1;
  const flowPhaseTotal = 2;

  return {
    blocks,
    displayWorkflow,
    showPhaseOverline,
    flowPhaseNumber,
    flowPhaseTotal,
    stepCurrent,
    stepTotal,
    progress,
    currentBlock,
  };
};
